import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
// import { yearsApi } from '../../services/api';
import type { Year } from '../../types';

const Nav = () => {
  const [years, setYears] = useState<Year[]>([]);
  const [isWinnersOpen, setIsWinnersOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Mock data for now - replace with actual API call when backend is ready
    const mockYears: Year[] = [
      { id: 4, year: '2023' },
      { id: 3, year: '2022' },
      { id: 2, year: '2021' },
      { id: 1, year: '2020' },
    ];
    setYears(mockYears);

    // Uncomment when API is ready:
    // yearsApi.getAll().then(setYears).catch(console.error);
  }, []);

  const isActive = (path: string) => location.pathname === path;
  const isHome = location.pathname === '/';

  // Dynamic colors based on page
  const textColor = isHome ? 'text-white' : 'text-gray-700';
  const hoverColor = 'hover:text-gold';
  const activeColor = 'text-gold';

  return (
    <nav className="hidden lg:block">
      <ul className="flex items-center space-x-1 xl:space-x-2 nav-spacing">
        <li>
          <Link 
            className={`relative px-3 py-2 ${textColor} transition-all duration-300 font-semibold uppercase text-sm tracking-wide group`}
            to="/"
            style={{
              display: 'inline-block',
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <span className="relative z-10">Home</span>
            <span 
              className={`absolute bottom-0 left-0 w-full h-0.5 transform origin-left transition-transform duration-300 ${isActive('/') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}
              style={{ 
                backgroundColor: '#ffba17',
                transformOrigin: 'left',
              }}
            />
          </Link>
        </li>
        <li>
          <Link 
            className={`relative px-3 py-2 ${textColor} transition-all duration-300 font-semibold uppercase text-sm tracking-wide group`}
            to="/about"
            style={{
              display: 'inline-block',
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <span className="relative z-10">About Us</span>
            <span 
              className={`absolute bottom-0 left-0 w-full h-0.5 transform origin-left transition-transform duration-300 ${isActive('/about') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}
              style={{ 
                backgroundColor: '#ffba17',
                transformOrigin: 'left',
              }}
            />
          </Link>
        </li>
        <li>
          <Link 
            className={`relative px-3 py-2 ${textColor} transition-all duration-300 font-semibold uppercase text-sm tracking-wide group`}
            to="/categories"
            style={{
              display: 'inline-block',
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <span className="relative z-10">Categories</span>
            <span 
              className={`absolute bottom-0 left-0 w-full h-0.5 transform origin-left transition-transform duration-300 ${isActive('/categories') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}
              style={{ 
                backgroundColor: '#ffba17',
                transformOrigin: 'left',
              }}
            />
          </Link>
        </li>
        <li 
          className="relative group"
          onMouseEnter={() => setIsWinnersOpen(true)}
          onMouseLeave={() => setIsWinnersOpen(false)}
        >
          <a 
            href="#" 
            onClick={(e) => e.preventDefault()} 
            className={`relative px-3 py-2 ${textColor} transition-all duration-300 font-semibold uppercase text-sm tracking-wide flex items-center gap-1`}
            style={{
              display: 'inline-flex',
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <span className="relative z-10">Winners</span>
            <i 
              className="fa-solid fa-chevron-down text-xs relative z-10" 
              style={{
                transition: 'transform 0.3s ease',
                transform: isWinnersOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            ></i>
            <span 
              className="absolute bottom-0 left-0 w-full h-0.5 transform origin-left transition-transform duration-300"
              style={{ 
                backgroundColor: '#ffba17',
                transformOrigin: 'left',
                transform: isWinnersOpen ? 'scaleX(1)' : 'scaleX(0)',
              }}
            />
          </a>
          <ul 
            className={`absolute top-full left-1/2 -translate-x-1/2 mt-4 bg-white shadow-2xl rounded-xl py-6 min-w-[360px] z-50 border-2 transition-all duration-500 ease-in-out ${
              isWinnersOpen 
                ? 'opacity-100 visible translate-y-0 scale-100' 
                : 'opacity-0 invisible -translate-y-4 scale-95'
            }`}
            style={{
              boxShadow: isWinnersOpen ? '0 20px 50px rgba(15, 27, 60, 0.3)' : 'none',
              borderColor: isWinnersOpen ? '#ffba17' : 'transparent',
              transformOrigin: 'top center'
            }}
          >
            <div className="px-5 py-2 border-b border-gray-100 mb-3">
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#0f1b3c' }}>
                <i className="fa-solid fa-trophy mr-2" style={{ color: '#ffba17' }}></i>
                Past Winners
              </p>
            </div>
            {years.map((year, index) => (
              <li key={year.id} className={index < years.length - 1 ? 'mb-2' : ''}>
                <Link 
                  className="group/item flex items-center justify-between px-5 py-4 text-gray-700 transition-all duration-300 relative overflow-hidden mx-3 rounded-lg hover:shadow-md" 
                  to={`/winners/${year.year}`}
                  style={{
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(90deg, #fff7ed 0%, #ffedd5 100%)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-md group-hover/item:shadow-xl group-hover/item:scale-110 transition-all duration-300"
                      style={{
                        background: 'linear-gradient(135deg, #ffba17 0%, #ffa500 100%)'
                      }}
                    >
                      {year.year.slice(-2)}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-semibold text-sm" style={{ color: '#0f1b3c' }}>TDA {year.year}</span>
                      <span className="text-xs text-gray-500">View Winners</span>
                    </div>
                  </div>
                  <i className="fa-solid fa-arrow-right opacity-0 group-hover/item:opacity-100 group-hover/item:translate-x-1 transition-all duration-300 ml-2" style={{ color: '#ffba17' }}></i>
                  {index === 0 && (
                    <span className="absolute top-2 right-2 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase" style={{ backgroundColor: '#ffba17' }}>Latest</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </li>
        {/* <li>
          <Link 
            className={`${textColor} ${hoverColor} transition-colors duration-300 font-medium uppercase text-sm tracking-wide ${isActive('/blog') ? activeColor : ''}`} 
            to="/blog"
          >
            Blog
          </Link>
        </li> */}
        <li>
          <Link 
            className={`relative px-3 py-2 ${textColor} transition-all duration-300 font-semibold uppercase text-sm tracking-wide group`}
            to="/contact"
            style={{
              display: 'inline-block',
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <span className="relative z-10">Contact Us</span>
            <span 
              className={`absolute bottom-0 left-0 w-full h-0.5 transform origin-left transition-transform duration-300 ${isActive('/contact') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}
              style={{ 
                backgroundColor: '#ffba17',
                transformOrigin: 'left',
              }}
            />
          </Link>
        </li>
        <li>
          <a 
            href="https://vote.digitalawards.co.tz" 
            target="_blank" 
            rel="noopener noreferrer"
            className="relative px-3 py-2 transition-all duration-300 font-semibold uppercase text-sm tracking-wide group"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              position: 'relative',
              color: isHome ? 'white' : '#374151',
              cursor: 'pointer',
            }}
          >
            <span className="relative z-10">Login</span>
            <i className="fa-solid fa-arrow-right-to-bracket text-xs relative z-10" style={{ fontSize: '0.75rem' }}></i>
            <span 
              className="absolute bottom-0 left-0 w-full h-0.5 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
              style={{ 
                backgroundColor: '#ffba17',
                transformOrigin: 'left',
              }}
            />
          </a>
        </li>
      </ul>
    </nav>
  );
};

export default Nav;
